import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdAccountBalanceWallet, MdWarningAmber } from 'react-icons/md';
import { toast } from 'react-toastify';
import styles from './style.module.scss';
import { useAuth } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartProvider';
import { useSaldo } from '../../contexts/SaldoContext';
import { useGirosGanhos } from '../../contexts/GirosGanhosContext';
import { useCheckoutTotals, formatCurrency } from '../../hooks/useCheckoutTotals';
import { api } from '../../services/apiClient';
import CheckoutBase from '../CheckoutBase';

const CreditoPrePago = ({ items, handleNextStep, couponCode, setCouponCode, validarCupon }) => {
  const [isSaldoRoute] = useState(() => window.location.href.includes('/saldo'));
  const [loading, setLoading] = useState(false);

  const { clearCart, coupon } = useCart();
  const { fetchSaldo, saldo } = useSaldo();
  const { mostrarGirosGanhos } = useGirosGanhos();
  const { user } = useAuth();
  const navigate = useNavigate();

  const resumo = useCheckoutTotals(items, coupon);
  // saldo do contexto é null até o GET /saldo responder; usa o do usuário nesse meio-tempo.
  const saldoAtual = saldo ?? user?.saldo ?? 0;
  const saldoSuficiente = saldoAtual >= resumo.total;
  const faltando = Math.max(resumo.total - saldoAtual, 0);

  const pagar = async () => {
    if (!saldoSuficiente) {
      toast.warn('Saldo insuficiente para este pedido.');
      return;
    }

    setLoading(true);
    try {
      const { status, data } = await api.post('/pagamento_com_saldo', {
        boloes: items,
        quantity: resumo.quantidade,
        total_desconto: resumo.total,
        cupom_desconto_id: coupon?.id,
        total: resumo.subtotal,
      });

      fetchSaldo();

      if (status === 200 && data.status === 'approved' && data.status_info === 'pago totalmente') {
        clearCart();
        toast.success('Compra feita com sucesso', { position: 'top-right', autoClose: 5000, theme: 'light' });
        handleNextStep('saldo');
        window.scrollTo(0, 0);
        navigate('/minha-conta');
        mostrarGirosGanhos(data.giros_creditados);
      }
    } catch (e) {
      console.log('e', e.message);
      toast.error('Não foi possível concluir o pagamento com saldo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <CheckoutBase
      resumo={resumo}
      couponCode={couponCode}
      setCouponCode={setCouponCode}
      validarCupon={validarCupon}
      mostrarCupom={!isSaldoRoute}
      loading={loading}
      onConfirm={pagar}
      confirmLabel={saldoSuficiente ? `Pagar ${formatCurrency(resumo.total)} com saldo` : 'Saldo insuficiente'}
    >
      <div className={styles.cardBox}>
        <div className={styles.sectionTitle}>
          <MdAccountBalanceWallet />
          <h2>Pagamento com saldo</h2>
        </div>
        <p className={styles.sectionDescription}>
          O valor sai direto do seu saldo na plataforma. Sem cartão, sem QR Code, liberação imediata.
        </p>

        <div className={`${styles.balanceCard} ${saldoSuficiente ? '' : styles.balanceShort}`}>
          <div className={styles.balanceRow}>
            <span>Saldo disponível</span>
            <strong>{formatCurrency(saldoAtual)}</strong>
          </div>
          <div className={styles.balanceRow}>
            <span>Valor deste pedido</span>
            <strong>{formatCurrency(resumo.total)}</strong>
          </div>
          <div className={`${styles.balanceRow} ${styles.balanceAfter}`}>
            <span>{saldoSuficiente ? 'Saldo após a compra' : 'Falta'}</span>
            <strong>{formatCurrency(saldoSuficiente ? saldoAtual - resumo.total : faltando)}</strong>
          </div>
        </div>

        {!saldoSuficiente && (
          <div className={styles.warning}>
            <MdWarningAmber />
            <div>
              <strong>Seu saldo não cobre este pedido</strong>
              <p>
                Faltam {formatCurrency(faltando)}. Você pode adicionar saldo ou escolher Pix
                ou cartão acima.
              </p>
              <button type='button' onClick={() => navigate('/saldo')}>Adicionar saldo</button>
            </div>
          </div>
        )}
      </div>
    </CheckoutBase>
  );
};

export default CreditoPrePago;
